import React, { useEffect, useRef, useState } from 'react';
import { useSpring, animated, config } from 'react-spring';
import { motion } from 'framer-motion';
import { MEDIA_URL } from '../../axios';
import projWorth from '../../Assets/Home/ProjWorth.svg';

const Counter = ({ n, start }) => {
  const { number } = useSpring({
    from: { number: 0 },
    number: start ? n : 0,
    delay: 200,
    config: config.molasses,
  });
  return <animated.span>{number.to((v) => v.toFixed(0))}</animated.span>;
};

const cardVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.15, duration: 0.6, ease: 'easeOut' },
  }),
};

const Stats = () => {
  const calendaricon = MEDIA_URL + 'Home/calendar.svg';
  const smileicon = MEDIA_URL + 'Home/smile.svg';
  const projectsicon = MEDIA_URL + 'Home/projects.svg';

  const statsRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  const stats = [
    { icon: calendaricon, value: 13, suffix: '+', label: 'Successful Years', border: 'border-cyan-200', bg: 'bg-cyan-50', iconBg: 'bg-cyan-200' },
    { icon: projectsicon, value: 150, suffix: '+', label: 'Successful Projects', border: 'border-indigo-200', bg: 'bg-indigo-50', iconBg: 'bg-indigo-200' },
    { icon: smileicon, value: 100, suffix: '+', label: 'Happy Clients', border: 'border-violet-200', bg: 'bg-violet-50', iconBg: 'bg-violet-200' },
    { icon: projWorth, value: 30, suffix: 'k Cr+', label: 'Worth Projects Delivered', border: 'border-purple-200', bg: 'bg-purple-50', iconBg: 'bg-purple-200' },
  ];

  return (
    <div ref={statsRef} className="relative py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-6">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.label}
            custom={i}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={cardVariant}
            whileHover={{ y: -14, scale: 1.06 }}
            transition={{ type: 'spring', stiffness: 300, damping: 18 }}
            className={`relative rounded-2xl border ${stat.border} ${stat.bg} shadow-sm hover:shadow-2xl p-6 py-8 flex flex-col items-center text-center gap-3 cursor-default`}
          >
            <div className={`w-14 h-14 rounded-full ${stat.iconBg} flex items-center justify-center`}>
              <img src={stat.icon} alt={stat.label} className="h-7" />
            </div>
            <b className="text-4xl font-bold darkblue flex items-baseline gap-0.5">
              <Counter n={stat.value} start={inView} />
              <span>{stat.suffix}</span>
            </b>
            <p className="text-sm text-stone-600">{stat.label}</p>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default Stats;